import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import BottomSheetOrDialog from '../../../../../components/BottomSheetOrDialog/BottomSheetOrDialog';
import { Typography, Box, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { getAverageValue } from '../../../../../utils/number';

const PAY_ROW_KEY = 'feature-hl-pay';
const CASH_FLOW_ROW_KEY = 'feature-hl-cash-flow';

const useStyles = makeStyles()(theme => ({
  tableContainer: {
    position: 'relative'
  },
  tableRow: {
    backgroundColor: '#ffffff',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      transform: 'translateY(-1px)'
    },
    '&.expanding': {
      animation: '$scaleUp 0.3s ease-out forwards'
    },
    '&.collapsing': {
      animation: '$scaleDown 0.3s ease-out forwards'
    }
  },
  '@keyframes scaleUp': {
    '0%': {
      transform: 'scale(1)'
    },
    '50%': {
      transform: 'scale(1.05)'
    },
    '100%': {
      transform: 'scale(1)'
    }
  },
  '@keyframes scaleDown': {
    '0%': {
      transform: 'scale(1)'
    },
    '50%': {
      transform: 'scale(0.95)'
    },
    '100%': {
      transform: 'scale(1)'
    }
  },
  '@keyframes fadeInConnector': {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  },
  labelColumn: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '2.625rem'
  },
  valueColumn: {
    width: '50%',
    backgroundColor: '#e8f4fd',
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    textAlign: 'center',
    fontWeight: 500,
    color: '#1a73e8'
  },
  emphasizedValueColumn: {
    width: '50%',
    backgroundColor: 'yellow',
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    textAlign: 'center'
  },
  discountColumn: {
    width: '50%',
    backgroundColor: '#e8f5e8',
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    textAlign: 'center',
    fontWeight: 500,
    color: '#137333',
    '&.noDiscount': {
      backgroundColor: 'transparent'
    }
  },

  currencyValue: {
    fontSize: '2.625rem',
    fontWeight: 600,
    display: 'inline-block',
    width: 'fit-content'
  },
  emphasizedCurrencyValue: {
    fontSize: '3rem',
    fontWeight: 900,
    color: 'green',
    display: 'inline-block',
    width: 'fit-content'
  },
  percentageValue: {
    fontSize: '2.625rem',
    fontWeight: 500
  },

  colReplacement: {
    minHeight: '4.738rem',
    backgroundColor: 'transparent'
  },
  connectorOverlay: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    overflow: 'visible',
    zIndex: 2
  },
  connectorPath: {
    fill: 'none',
    stroke: '#e53935',
    strokeLinecap: 'round',
    animation: '$fadeInConnector 0.4s ease-out forwards'
  }
}));

const getHighlightedDate = props => {
  const {
    yearlyData,
    years,
    loan,
    base,
    bankLoanInterest,
    firstYearBonus,
    overrideLoanInterest,
    termLoanPercentage,
    principal,
    selectedRow = 1
  } = props;
  const targetYearCount = years[selectedRow];
  return {
    'feature-hl-total-premium': {
      display: true,
      value: principal,
      discount: {
        type: 'currency',
        value: firstYearBonus
      }
    },
    'feature-hl-pay': {
      display: true,
      value: base
    },
    'feature-hl-loan': {
      display: true,
      value: loan
    },
    'feature-hl-total-interest': {
      display: true,
      value: yearlyData.expenseForTable[selectedRow],
      discount: (() => {
        const interestRate = overrideLoanInterest ? getAverageValue(termLoanPercentage) : bankLoanInterest;
        return {
          type: 'percentage',
          value: interestRate / 100,
          monthlyAmount: (loan * interestRate) / (12 * 100)
        };
      })()
    },
    'feature-hl-target-year-index': {
      display: false,
      value: selectedRow
    },
    'feature-hl-target-year-count': {
      display: false,
      value: targetYearCount
    },
    'feature-hl-total-projection-amount': {
      display: true,
      title: {
        year: targetYearCount
      },
      value: yearlyData.totalAmount[selectedRow]
    },
    'feature-hl-cash-flow': {
      display: true,
      emphasized: true,
      value: yearlyData.cashValueMinusLoan[selectedRow]
    },
    'feature-hl-net-gain': {
      display: true,
      value: yearlyData.returnsForTable[selectedRow],
      discount: {
        type: 'percentage',
        value: yearlyData.profitRateWithYearsToShow[selectedRow] / 100
      }
    }
  };
};
const formatCurrency = value => {
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  return value;
};

const formatPercentage = value => {
  if (typeof value === 'number' && value < 1) {
    return `${(value * 100).toFixed(2)}%`;
  }
  return value;
};

const RenderDisplayGrid = ({
  intl,
  rowKey,
  title,
  value,
  discount,
  emphasized,
  showDetails,
  onClick,
  classes,
  animationClass,
  valueRef
}) => {
  const discountType = discount?.type;
  const discountValue = discount?.value;
  return (
    <Paper className={clsx(classes.tableRow, animationClass)} onClick={onClick} elevation={1}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box style={{ width: 'fit-content', minWidth: '20%' }}>
          <Typography className={classes.labelColumn}>{intl.formatMessage({ id: rowKey }, title)}</Typography>
        </Box>
        {showDetails && (
          <Box
            style={{ gap: '10px' }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Box className={emphasized ? classes.emphasizedValueColumn : classes.valueColumn}>
              <Typography
                ref={valueRef}
                className={emphasized ? classes.emphasizedCurrencyValue : classes.currencyValue}
              >
                {formatCurrency(value)}
              </Typography>
            </Box>
            <Box className={clsx(classes.discountColumn, discount ? '' : ' noDiscount')}>
              <Typography className={classes.percentageValue}>
                {discountType !== undefined && discountType !== null
                  ? intl.formatMessage(
                      { id: `${rowKey}-discount` },
                      {
                        value:
                          discountType === 'currency'
                            ? formatCurrency(discountValue)
                            : `${(discountValue * 100).toFixed(2)}%`,
                        monthlyAmount:
                          discount?.monthlyAmount !== undefined && discount?.monthlyAmount !== null
                            ? formatCurrency(discount.monthlyAmount)
                            : undefined
                      }
                    )
                  : ''}
              </Typography>
            </Box>
          </Box>
        )}
        {!showDetails && <Box className={classes.colReplacement} />}
      </Box>
    </Paper>
  );
};

const buildPayToCashFlowPath = (containerEl, payEl, cashFlowEl) => {
  const container = containerEl.getBoundingClientRect();
  const from = payEl.getBoundingClientRect();
  const to = cashFlowEl.getBoundingClientRect();

  // Orthogonal bracket: right → down → left (matches design mockup).
  // Anchor just past the number text (number right + 24px).
  const startX = from.right - container.left + 24;
  const startY = from.top + from.height / 2 - container.top;
  const endX = to.right - container.left + 24;
  const endY = to.top + to.height / 2 - container.top;
  const available = Math.max(0, container.width - Math.max(startX, endX) - 12);
  const elbowX = Math.max(startX, endX) + Math.min(100, available);

  return `M ${startX} ${startY} L ${elbowX} ${startY} L ${elbowX} ${endY} L ${endX} ${endY}`;
};

const ClickableContentDisplayDialog = ({ open, onClose, intl, ...props }) => {
  const { classes } = useStyles();
  const [displayContent, setDisplayContent] = useState([]);
  const [animatingRows, setAnimatingRows] = useState(new Set());
  const [showConnector, setShowConnector] = useState(false);
  const [connectorPath, setConnectorPath] = useState('');
  const containerRef = useRef(null);
  const payValueRef = useRef(null);
  const cashFlowValueRef = useRef(null);

  const data = getHighlightedDate(props);

  const displayData = Object.entries(data).reduce((acc, [key, value]) => {
    if (value.display) {
      acc[key] = {
        value: value.value,
        discount: value.discount,
        title: value.title,
        emphasized: value.emphasized
      };
    }
    return acc;
  }, {});

  const displayKeys = Object.keys(displayData);
  const allRowsShown = displayKeys.length > 0 && displayKeys.every(key => displayContent.includes(key));

  const handleClick = id => {
    // All rows already open → next click reveals the pay → cash-flow line.
    if (allRowsShown && !showConnector) {
      setShowConnector(true);
      return;
    }

    if (displayContent.includes(id)) {
      setShowConnector(false);
      setDisplayContent(displayContent.filter(content => content !== id));
    } else {
      setDisplayContent([...displayContent, id]);
      setAnimatingRows(prev => new Set([...prev, `${id}-expanding`]));
      setTimeout(() => {
        setAnimatingRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${id}-expanding`);
          return newSet;
        });
      }, 300);
    }
  };

  const updateConnectorPath = useCallback(() => {
    if (
      !showConnector ||
      !allRowsShown ||
      !containerRef.current ||
      !payValueRef.current ||
      !cashFlowValueRef.current
    ) {
      setConnectorPath('');
      return;
    }
    setConnectorPath(buildPayToCashFlowPath(containerRef.current, payValueRef.current, cashFlowValueRef.current));
  }, [allRowsShown, showConnector]);

  useLayoutEffect(() => {
    updateConnectorPath();
  }, [updateConnectorPath, displayContent, showConnector]);

  useEffect(() => {
    if (!showConnector || !allRowsShown) return undefined;
    window.addEventListener('resize', updateConnectorPath);
    return () => window.removeEventListener('resize', updateConnectorPath);
  }, [allRowsShown, showConnector, updateConnectorPath]);

  useEffect(() => {
    if (!allRowsShown) setShowConnector(false);
  }, [allRowsShown]);

  return (
    <BottomSheetOrDialog
      open={open}
      onClose={() => {
        setDisplayContent([]);
        setAnimatingRows(new Set());
        setShowConnector(false);
        setConnectorPath('');
        onClose();
      }}
      // header={intl.formatMessage({ id: 'feature-hl-dialog-header' })}
      DialogProps={{
        maxWidth: 'md',
        fullWidth: true,
        fullScreen: false
      }}
      content={
        <Box ref={containerRef} className={classes.tableContainer}>
          {Object.entries(displayData).map(([key, value]) => {
            const animationClass = animatingRows.has(`${key}-expanding`)
              ? 'expanding'
              : animatingRows.has(`${key}-collapsing`)
              ? 'collapsing'
              : '';
            const valueRef =
              key === PAY_ROW_KEY ? payValueRef : key === CASH_FLOW_ROW_KEY ? cashFlowValueRef : undefined;
            return (
              <RenderDisplayGrid
                key={key}
                intl={intl}
                rowKey={key}
                value={value.value}
                discount={value.discount ?? null}
                emphasized={value.emphasized}
                showDetails={displayContent.includes(key)}
                classes={classes}
                onClick={() => handleClick(key)}
                animationClass={animationClass}
                title={value.title}
                valueRef={valueRef}
              />
            );
          })}
          {connectorPath && (
            <svg className={classes.connectorOverlay} aria-hidden="true">
              <defs>
                <marker
                  id="pfa-pay-to-cashflow-arrow"
                  markerWidth="16"
                  markerHeight="16"
                  refX="14"
                  refY="8"
                  orient="auto"
                  markerUnits="userSpaceOnUse"
                >
                  <path d="M0,0 L16,8 L0,16 Z" fill="#e53935" />
                </marker>
              </defs>
              <path
                key={connectorPath}
                d={connectorPath}
                className={classes.connectorPath}
                strokeWidth="6"
                markerEnd="url(#pfa-pay-to-cashflow-arrow)"
              />
            </svg>
          )}
        </Box>
      }
    />
  );
};

export default ClickableContentDisplayDialog;
